/**
 * EventsTab - Client-linked events: appointments, tasks, deadlines, reminders.
 * Part of Event Management: scheduling, workflow tracking, team collaboration.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FlagIcon from '@mui/icons-material/Flag';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import api from '../api';

const EVENT_TYPES = [
  { value: 'appointment', label: 'Appointment', icon: <EventIcon /> },
  { value: 'task', label: 'Task', icon: <ScheduleIcon /> },
  { value: 'deadline', label: 'Deadline', icon: <FlagIcon /> },
  { value: 'reminder', label: 'Reminder', icon: <NotificationsIcon /> },
];

const STATUS_COLORS = {
  scheduled: 'primary',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'default',
  overdue: 'error',
};

const formatDate = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: d.getHours() || d.getMinutes() ? '2-digit' : undefined,
    minute: d.getMinutes() ? '2-digit' : undefined,
  });
};

const EventsTab = ({ clientId, refreshTrigger }) => {
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'task',
    status: 'scheduled',
    priority: 'medium',
    start_datetime: '',
    end_datetime: '',
    due_date: '',
    all_day: false,
    assigned_to: '',
    reminder_minutes_before: [],
  });

  const fetchEvents = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/multi-tenant/clients/${clientId}/events/`, {
        params: { upcoming: 'false', limit: 100 },
      });
      setEvents(res.data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/multi-tenant/users/');
      setUsers(Array.isArray(res.data) ? res.data : res.data?.users || []);
    } catch {
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [clientId, refreshTrigger]);

  useEffect(() => {
    if (showForm) fetchUsers();
  }, [showForm]);

  const handleCreate = async () => {
    try {
      await api.post('/api/multi-tenant/events/', {
        ...formData,
        client: clientId,
        start_datetime: formData.start_datetime || null,
        end_datetime: formData.end_datetime || null,
        due_date: formData.due_date || null,
        assigned_to: formData.assigned_to || null,
      });
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        event_type: 'task',
        status: 'scheduled',
        priority: 'medium',
        start_datetime: '',
        end_datetime: '',
        due_date: '',
        all_day: false,
        assigned_to: '',
        reminder_minutes_before: [],
      });
      fetchEvents();
    } catch {
      // Error handled silently or via snackbar
    }
  };

  const upcomingEvents = events.filter(
    (e) =>
      e.status !== 'completed' &&
      e.status !== 'cancelled' &&
      (e.start_datetime || e.due_date || e.end_datetime)
  );
  const pastEvents = events.filter(
    (e) => e.status === 'completed' || e.status === 'cancelled'
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
          >
            Add Event
          </Button>
        </Box>
      </Grid>

      {loading ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 4, boxShadow: 3, borderRadius: 2, textAlign: 'center' }}>
            <Typography color="textSecondary">Loading events...</Typography>
          </Paper>
        </Grid>
      ) : (
        <>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column', boxShadow: 'none' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Upcoming
              </Typography>
              {upcomingEvents.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <CalendarTodayIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1.5 }} />
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    No upcoming events
                  </Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowForm(true)} sx={{ textTransform: 'none' }}>
                    Schedule a meeting
                  </Button>
                </Box>
              ) : (
                <List dense>
                  {upcomingEvents.map((ev) => (
                    <ListItem key={ev.id}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {EVENT_TYPES.find((t) => t.value === ev.event_type)?.icon}
                            <Typography variant="body1" fontWeight="medium">
                              {ev.title}
                            </Typography>
                            <Chip
                              size="small"
                              label={ev.event_type}
                              color={STATUS_COLORS[ev.status] || 'default'}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="textSecondary">
                              {formatDate(ev.start_datetime || ev.due_date || ev.end_datetime)}
                              {ev.assigned_to_name && (
                                <>
                                  {' • '}
                                  <PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  {ev.assigned_to_name}
                                </>
                              )}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column', boxShadow: 'none' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Past / Completed
              </Typography>
              {pastEvents.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <HistoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1.5 }} />
                  <Typography color="text.secondary">No past events</Typography>
                </Box>
              ) : (
                <List dense>
                  {pastEvents.slice(0, 10).map((ev) => (
                    <ListItem key={ev.id}>
                      <ListItemText
                        primary={ev.title}
                        secondary={`${formatDate(ev.start_datetime || ev.due_date)} • ${ev.status}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </>
      )}

      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                Event details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                >
                  {EVENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t.icon}
                        {t.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.all_day}
                    onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                  />
                }
                label="All day"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                Schedule
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Start"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.start_datetime}
                onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Due / End"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.due_date || formData.end_datetime}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    due_date: e.target.value,
                    end_datetime: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Assign to</InputLabel>
                <Select
                  label="Assign to"
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!formData.title.trim()}
          >
            Create Event
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default EventsTab;
